import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useAccountTimezone } from '@/hooks/useAccountTimezone'
import { formatPauseRemaining } from '@/utils/formatAutomationPause'

type CountdownVariant = 'countdown' | 'window' | 'blocked' | 'starting'

type FollowUpStateRow = {
  id: string
  status: 'active' | 'completed' | 'paused' | 'blocked' | 'goal_met' | 'stopped'
  next_fire_at: string | null
  next_fire_reason: string | null
  last_error: string | null
  enrollment_source: string | null
}

export interface FollowUpCountdownState {
  visible: boolean
  label: string
  variant: CountdownVariant
}

const HIDDEN_STATE: FollowUpCountdownState = {
  visible: false,
  label: '',
  variant: 'countdown',
}

interface Params {
  contactId?: string | null
  assistantIsActive?: boolean | null
}

export function useContactFollowUpCountdown({ contactId, assistantIsActive }: Params): FollowUpCountdownState {
  const { user } = useAuth()
  const { t } = useTranslation('common')
  const [rows, setRows] = useState<FollowUpStateRow[]>([])
  // Read from the shared account preference instead of querying `accounts` per contact.
  // `format` is memoized on timezone + locale, so it is the honest dependency here.
  const { format: formatInTz } = useAccountTimezone()
  const [nowMs, setNowMs] = useState(Date.now())

  // A inscrição manual vale mesmo com o assistente desligado no contato, então
  // não dá para gatear a busca por `assistantIsActive` — é o próprio resultado
  // que diz se existe cadência manual em curso. A visibilidade é decidida abaixo.
  useEffect(() => {
    if (!contactId || !supabase) {
      setRows([])
      return
    }
    const client = supabase

    let mounted = true
    const loadStates = async () => {
      const { data, error } = await client
        .from('account_contact_follow_up_states')
        .select('id, status, next_fire_at, next_fire_reason, last_error, enrollment_source')
        .eq('account_contact_id', contactId)
        .order('next_fire_at', { ascending: true })
        .limit(10)

      if (error) {
        console.error('Failed to load follow-up countdown states:', error)
        return
      }

      if (mounted) {
        setRows((data || []) as FollowUpStateRow[])
      }
    }

    loadStates()

    const channel = client
      .channel(`followup-countdown-${contactId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'account_contact_follow_up_states',
          filter: `account_contact_id=eq.${contactId}`,
        },
        () => loadStates()
      )
      .subscribe()

    return () => {
      mounted = false
      client.removeChannel(channel)
    }
  }, [contactId])

  useEffect(() => {
    if (!contactId) return
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000)
    return () => window.clearInterval(interval)
  }, [contactId])

  return useMemo(() => {
    // Inscrição manual não depende do assistente estar ativo no contato.
    const hasManualEnrollment = rows.some((row) => row.enrollment_source === 'manual')

    if (!contactId || (!assistantIsActive && !hasManualEnrollment)) {
      return HIDDEN_STATE
    }

    const activeWithFireAt = rows.find(
      (row) => row.status === 'active' && !!row.next_fire_at
    )

    if (activeWithFireAt?.next_fire_at) {
      const fireMs = new Date(activeWithFireAt.next_fire_at).getTime()
      const diffMs = fireMs - nowMs

      if (diffMs <= 0) {
        return {
          visible: true,
          label: t('followUp.starting'),
          variant: 'starting',
        }
      }

      const remaining = formatPauseRemaining(diffMs)

      if (activeWithFireAt.next_fire_reason === 'outside_window') {
        const localTime = formatInTz(activeWithFireAt.next_fire_at, {
          hour: '2-digit',
          minute: '2-digit',
        })

        return {
          visible: true,
          label: t('followUp.waitingWindow', { time: localTime, remaining }),
          variant: 'window',
        }
      }

      return {
        visible: true,
        label: t('followUp.countdown', { remaining }),
        variant: 'countdown',
      }
    }

    const goalMet = rows.find((row) => row.status === 'goal_met')
    if (goalMet) {
      return {
        visible: true,
        label: t('followUp.goalMet'),
        variant: 'starting',
      }
    }

    const paused = rows.find((row) => row.status === 'paused')
    if (paused) {
      const pausedLabel =
        paused.next_fire_reason === 'human_active'
          ? t('followUp.pausedHuman')
          : paused.next_fire_reason === 'excluded_tag'
            ? t('followUp.pausedExcludedTag')
            : t('followUp.paused')
      return {
        visible: true,
        label: pausedLabel,
        variant: 'window',
      }
    }

    const blocked = rows.find((row) => row.status === 'blocked')
    if (blocked) {
      return {
        visible: true,
        label: blocked.last_error || t('followUp.blocked'),
        variant: 'blocked',
      }
    }

    return HIDDEN_STATE
  }, [rows, contactId, assistantIsActive, nowMs, formatInTz, t])
}
